import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import type { ColumnDef } from "@tanstack/react-table";
import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import ReactMarkdown from "react-markdown";
import rehypeSanitize from "rehype-sanitize";
import { activatePromptVersion, listPrompts } from "@/api/lookups";
import type { PromptVersionResponse, UUID } from "@/api/types";
import { Button } from "@/components/ui/button";
import { DataTable } from "@/components/table/DataTable";
import { PromptCreateForm } from "@/components/registries/PromptCreateForm";
import { PromptDiff } from "@/components/registries/PromptDiff";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";

export interface PromptsAdminProps {
  meetingTypeId: UUID;
}

export function PromptsAdmin({ meetingTypeId }: PromptsAdminProps) {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const [diffWith, setDiffWith] = useState<UUID | null>(null);
  const [view, setView] = useState<"full" | "diff">("full");

  const prompts = useQuery({
    queryKey: ["prompts", meetingTypeId],
    queryFn: () => listPrompts(meetingTypeId),
  });
  const activate = useMutation({
    mutationFn: (pv: PromptVersionResponse) => activatePromptVersion(pv),
    onSuccess: () =>
      queryClient.invalidateQueries({
        queryKey: ["prompts", meetingTypeId],
      }),
  });

  const versions = useMemo(() => prompts.data ?? [], [prompts.data]);
  const active = useMemo(
    () => versions.find((v) => v.is_active) ?? null,
    [versions],
  );
  const diffTarget = versions.find((v) => v.id === diffWith) ?? null;

  const columns: ColumnDef<PromptVersionResponse, unknown>[] = useMemo(
    () => [
      {
        id: "version",
        header: t("adminPrompts.columns.version"),
        cell: (info) => `#${info.row.original.version}`,
      },
      {
        id: "createdAt",
        header: t("adminPrompts.columns.createdAt"),
        cell: (info) =>
          new Date(info.row.original.created_at).toLocaleString("ru"),
      },
      {
        id: "active",
        header: t("adminPrompts.columns.active"),
        cell: (info) =>
          info.row.original.is_active ? t("common.yes") : t("common.no"),
      },
      {
        id: "preview",
        header: t("adminPrompts.columns.preview"),
        cell: (info) => (
          <code className="text-xs">{info.row.original.text.slice(0, 80)}</code>
        ),
      },
      {
        id: "actions",
        header: "",
        cell: (info) => (
          <div className="flex gap-2">
            {!info.row.original.is_active ? (
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={() => activate.mutate(info.row.original)}
                disabled={activate.isPending}
                data-testid={`activate-prompt-${info.row.original.id}`}
              >
                {t("adminPrompts.activateButton")}
              </Button>
            ) : null}
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={() => {
                setDiffWith(info.row.original.id);
                setView("diff");
              }}
              data-testid={`diff-prompt-${info.row.original.id}`}
            >
              diff
            </Button>
          </div>
        ),
      },
    ],
    [t, activate],
  );

  return (
    <div className="space-y-4">
      <header>
        <h1 className="text-xl font-semibold">{t("adminPrompts.title")}</h1>
        <p className="text-sm text-muted-foreground">
          {t("adminPrompts.subtitle")}
        </p>
      </header>
      <DataTable
        data={versions}
        columns={columns}
        testId="prompts-table"
        isLoading={prompts.isLoading}
      />
      {active !== null ? (
        <Tabs
          value={view}
          onValueChange={(v) => setView(v as "full" | "diff")}
          data-testid="prompt-view-tabs"
        >
          <TabsList>
            <TabsTrigger value="full" data-testid="prompt-view-full-trigger">
              {t("adminPrompts.viewFullText")}
            </TabsTrigger>
            <TabsTrigger
              value="diff"
              disabled={diffTarget === null || diffTarget.id === active.id}
              data-testid="prompt-view-diff-trigger"
            >
              {t("adminPrompts.viewDiff")}
            </TabsTrigger>
          </TabsList>
          <TabsContent value="full">
            <section
              className="rounded-md border p-3"
              aria-label="prompt full text"
              data-testid="prompt-full-text"
            >
              <h2 className="text-sm font-medium">
                {t("adminPrompts.activeVersionHeader", {
                  version: active.version,
                })}
              </h2>
              <div
                className="prose prose-sm mt-2 max-w-none whitespace-pre-wrap break-words text-sm"
                data-testid="prompt-full-text-body"
              >
                <ReactMarkdown rehypePlugins={[rehypeSanitize]}>
                  {active.text}
                </ReactMarkdown>
              </div>
            </section>
          </TabsContent>
          <TabsContent value="diff">
            {diffTarget !== null && diffTarget.id !== active.id ? (
              <section
                className="rounded-md border p-3"
                aria-label="prompt diff"
                data-testid="prompt-diff"
              >
                <h2 className="text-sm font-medium">
                  #{active.version} vs #{diffTarget.version}
                </h2>
                <PromptDiff a={active.text} b={diffTarget.text} />
              </section>
            ) : (
              <p
                className="rounded-md border p-3 text-sm text-muted-foreground"
                data-testid="prompt-diff-empty"
              >
                {t("adminPrompts.diffPickHint")}
              </p>
            )}
          </TabsContent>
        </Tabs>
      ) : null}
      <PromptCreateForm
        meetingTypeId={meetingTypeId}
        currentText={active?.text ?? null}
        onCreated={() =>
          queryClient.invalidateQueries({
            queryKey: ["prompts", meetingTypeId],
          })
        }
      />
    </div>
  );
}
